import React from 'react';
import SlideShell from '../../SlideShell';
import PatientFileDeclaration from '../../../../components/Onboarding/PatientFileDeclaration';
import Grid from '../../../../components/Onboarding/Charts/Grid';
import BulletList from '../../../../components/Onboarding/BulletList';

export default function GoodTasteDisplaySlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  nextLabel = 'Continue',

  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',

  // grid content
  images = [],
  gridGap = 6,
  showTitles = false,

  // NEW: user selections to prioritize and badge
  likedImages = [],
  dislikedImages = [],

  // bullet list
  bulletPoints = [
    'Dense hair preference',
    'Masculine hairline preference',
    'Sharp contrast preference',
  ],
  bulletDelayMs = 300,

  // Footer Patient File controls (forwarded to SlideShell) – no defaults here
  showPatientFileDeclaration,
  patientFileDynamicText,
  patientFileDurationMs,
  patientFileClassName,

}) {
  const isDark = theme === 'dark';
  const cardBg = isDark ? 'bg-gray-900/40' : 'bg-gray-50';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  // Build grouped list: liked first, then disliked; fallback to provided images if none
  const toHref = React.useCallback((s) => {
    try { return new URL(String(s), typeof window !== 'undefined' ? window.location.href : 'http://localhost').href; }
    catch { return String(s ?? ''); }
  }, []);

  // Compute final, conflict-free sets (like wins over dislike if overlap remains)
  const rawLikeList = React.useMemo(() => (likedImages || []).filter(Boolean), [likedImages]);
  const rawDislikeList = React.useMemo(() => (dislikedImages || []).filter(Boolean), [dislikedImages]);

  // Only allow images that are in the provided images array
  const allowedSet = React.useMemo(() => new Set((images || []).map(toHref)), [images, toHref]);

  const likeSet = React.useMemo(() => {
    const set = new Set();
    rawLikeList.forEach((src) => {
      const key = toHref(src);
      if (allowedSet.has(key)) set.add(key);
    });
    return set;
  }, [rawLikeList, toHref, allowedSet]);
  const dislikeSet = React.useMemo(() => {
    const set = new Set();
    rawDislikeList.forEach((src) => {
      const key = toHref(src);
      if (allowedSet.has(key)) set.add(key);
    });
    // remove any that are liked
    likeSet.forEach((k) => set.delete(k));
    return set;
  }, [rawDislikeList, likeSet, toHref, allowedSet]);

  const uniqueOrder = React.useMemo(() => {
    const result = [];
    const seen = new Set();
    const push = (src) => { if (!src) return; if (seen.has(src)) return; const key = toHref(src); if (!allowedSet.has(key)) return; seen.add(src); result.push(src); };

    // liked first (original order), then disliked, then remaining images
    rawLikeList.forEach(push);
    rawDislikeList.forEach((src) => { const key = toHref(src); if (!likeSet.has(key)) push(src); });
    images.forEach(push);
    return result;
  }, [rawLikeList, rawDislikeList, images, likeSet, toHref, allowedSet]);

  const getOverlay = React.useCallback((i, img) => {
    const src = img?.src; if (!src) return null;
    const key = toHref(src);
    const isLiked = likeSet.has(key);
    const isDisliked = !isLiked && dislikeSet.has(key);
    if (!isLiked && !isDisliked) return null;

    const bg = isLiked ? 'rgba(16,185,129,1)' : 'rgba(239,68,68,1)';
    const Icon = isLiked ? (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path d="M5 12l4 4 10-10" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path d="M6 6l12 12" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18 6L6 18" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );

    return (
      <div className="absolute top-1 right-1">
        <div
          className="flex items-center justify-center rounded-full shadow"
          style={{ width: 22, height: 22, backgroundColor: bg }}
        >
          {Icon}
        </div>
      </div>
    );
  }, [likeSet, dislikeSet, toHref]);

  return (
    <div className={`min-h-screen ${pageBg}`}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={onNext}
        onBack={onBack}
        nextLabel={nextLabel}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        showPatientFile={showPatientFileDeclaration}
        patientFileProps={{
        dynamicText: patientFileDynamicText,
        durationMs: patientFileDurationMs,
        className: patientFileClassName,
        }}
      >
        {/* Grid card */}
        <div className="w-full">
          <div className={`rounded-2xl ${cardBg} p-2 w-full`}>
            <Grid
              theme={theme}
              images={uniqueOrder}
              gap={gridGap}
              showTitles={showTitles}
              cellScale={0.84}
              sizeBasis="width"
              animate
              getOverlay={getOverlay}
            />
          </div>
        </div>

        {bulletPoints?.length > 0 && (
          <BulletList
            items={bulletPoints}
            delayMs={bulletDelayMs}
            theme={theme}
            className="px-2 mb-8 md:mb-10"
          />
        )}

      </SlideShell>
    </div>
  );
}
